import { localDateOf } from '@servwell/shared';
import { minutesInTz, MORNING_ENDS_AT } from '../../lib/format';

/**
 * The week board's geometry and arithmetic, as pure functions (121, ref 23413635).
 *
 * ⭐ Pure and separate from the component for the reason `timeline.ts` and `nextStop.ts` are:
 * `apps/web` runs its tests in node and cannot render, so the numbers a dispatcher reads off
 * a grid are the half a test can hold to account. What is left in the component is markup.
 *
 * 🔴 **THERE IS STILL NO CAPACITY MODEL, and this module refuses to invent one.** No
 * working-hours, business-hours or availability data exists anywhere in `packages/db` — the
 * same absence that killed utilization % on the day board (113 F3). "How full is the week"
 * is the question this board exists to answer and "full" needs a denominator, so the bar
 * compares each cell to the **busiest cell in view** and nothing here returns a percentage.
 * That is a relative shape, not a measure of anyone's limit, and the caption saying so is
 * load-bearing: drop it and the board silently starts claiming capacity.
 */

/** The unassigned lane's id. Not a user id — no user is "unassigned". */
export const UNASSIGNED_LANE = '__unassigned__';

export interface WeekVisit {
  id: string;
  scheduledStartAt: string | Date | null;
  scheduledEndAt: string | Date | null;
  assigneeUserIds: readonly string[];
}

/** One technician × one day. */
export interface WeekCell {
  laneId: string;
  day: string;
  stops: number;
  /** Booked minutes — the sum of each stop's planned LENGTH, not its span to the next. */
  bookedMinutes: number;
  /** Minutes starting before noon at the shop, and from noon on. Decision 4's split. */
  amMinutes: number;
  pmMinutes: number;
  visitIds: string[];
}

export interface WeekLane {
  laneId: string;
  /** Null for the unassigned lane, which is not a person. */
  userId: string | null;
  name: string;
  vanless: boolean;
}

export interface WeekGrid {
  lanes: WeekLane[];
  days: string[];
  /** Keyed `${laneId}|${day}` — sparse, because most cells are empty on a real week. */
  cells: Map<string, WeekCell>;
  /**
   * The busiest cell in VIEW, in booked minutes — the load bar's denominator.
   *
   * ⚠️ Zero when the week is empty, and every caller must treat that as "draw no bar" rather
   * than dividing by it. A quiet week makes small numbers look large, which is honest but
   * surprising; it is the caption's job to say so.
   */
  busiestMinutes: number;
}

/** Every calendar day from `from`, `count` long, as `YYYY-MM-DD`. */
export function weekDays(from: string, count = 7): string[] {
  const out: string[] = [];
  for (let i = 0; i < count; i += 1) {
    out.push(
      new Date(Date.parse(`${from}T00:00:00Z`) + i * 86_400_000)
        .toISOString()
        .slice(0, 10),
    );
  }
  return out;
}

/**
 * The Monday of the week containing `iso`.
 *
 * ⚠️ **Monday-first, hard-coded, and named rather than configurable.** The reference is
 * Sunday-first; 120 drew Monday and was approved as drawn. No org setting for week-start
 * exists, and inventing one here would be a data-model decision made inside a view.
 */
export function weekStart(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  // getUTCDay: 0=Sun … 6=Sat. Monday-first means Sunday belongs to the week BEFORE it.
  const shift = (d.getUTCDay() + 6) % 7;
  return new Date(d.getTime() - shift * 86_400_000).toISOString().slice(0, 10);
}

const at = (v: string | Date | null): number | null => {
  if (v === null) return null;
  const t = new Date(v).getTime();
  return Number.isFinite(t) ? t : null;
};

/**
 * A stop's planned length in minutes, or null when the office never set an end.
 *
 * ⚠️ Null is the COMMON case, not an edge — neither creation sheet sets an end. A visit with
 * no end contributes to the stop COUNT and contributes nothing to booked hours, because
 * inventing a default (an hour? two?) would put a number in a column labelled "booked" that
 * the office never booked. `visitTiming.ts` made the same call for the same reason.
 *
 * ⚠️ Clamped at zero: `scheduled_end_at` has no CHECK forcing it after its start, and a row
 * saved back-to-front would otherwise SUBTRACT from a lane's total.
 */
export function plannedMinutes(v: WeekVisit): number | null {
  const s = at(v.scheduledStartAt);
  const e = at(v.scheduledEndAt);
  if (s === null || e === null) return null;
  return Math.max(0, Math.round((e - s) / 60_000));
}

/**
 * Bucket a week of visits into (lane × day).
 *
 * ⚠️ `tz` is the SHOP's zone and decides two different things: which DAY COLUMN a stop falls
 * in, and whether it counts as morning or afternoon. Using the browser's would put a
 * dispatcher in another timezone's evening stops in the wrong column entirely — a whole
 * visit in the wrong place, not merely a mislabelled bar.
 *
 * ⚠️ A visit with **several assignees lands in EVERY one of their lanes**. It is one stop for
 * each of them, because the question a lane answers is "what does this person's Thursday look
 * like", and a two-person job is on both their Thursdays. The board therefore does NOT sum to
 * the shop's visit count, which is why no total is drawn anywhere on it.
 *
 * ⚠️ A visit with **no assignees** lands in the unassigned lane — a week's worth of unbooked
 * work in one glance instead of seven (decision 2).
 *
 * ⚠️ A visit with **no `scheduledStartAt`** is dropped, because it belongs in no column.
 * `visit.board` already filters on that column so this is defence rather than a live case,
 * and it is stated because "not on the board" and "does not exist" look identical on a grid.
 */
export function buildWeekGrid(
  visits: readonly WeekVisit[],
  lanes: readonly WeekLane[],
  days: readonly string[],
  tz: string,
): WeekGrid {
  const inView = new Set(days);
  const cells = new Map<string, WeekCell>();
  const key = (laneId: string, day: string) => `${laneId}|${day}`;

  const cellFor = (laneId: string, day: string): WeekCell => {
    const k = key(laneId, day);
    let c = cells.get(k);
    if (!c) {
      c = {
        laneId,
        day,
        stops: 0,
        bookedMinutes: 0,
        amMinutes: 0,
        pmMinutes: 0,
        visitIds: [],
      };
      cells.set(k, c);
    }
    return c;
  };

  const laneIds = new Set(lanes.map((l) => l.laneId));

  for (const v of visits) {
    if (v.scheduledStartAt === null || at(v.scheduledStartAt) === null) continue;
    /**
     * ⚠️ `localDateOf`, NOT `dayInTz`. The latter wraps this in `dateOnly`, which is a
     * DISPLAY formatter — it returns "Aug 27", so every cell key would have missed every
     * day in `days` and the grid would have come back silently empty.
     */
    const day = localDateOf(new Date(v.scheduledStartAt), tz);
    if (!inView.has(day)) continue;

    const mins = plannedMinutes(v);
    const startsAm = minutesInTz(v.scheduledStartAt, tz) < MORNING_ENDS_AT;

    const targets =
      v.assigneeUserIds.length === 0
        ? [UNASSIGNED_LANE]
        : // ⚠️ An assignee with no lane is DROPPED rather than inventing one. Lanes come from
          // the same window's roster read; a stray id means the two reads disagree, and a
          // phantom lane would be worse than a missing stop because nothing explains it.
          v.assigneeUserIds.filter((id) => laneIds.has(id));

    for (const laneId of targets) {
      const c = cellFor(laneId, day);
      c.stops += 1;
      c.visitIds.push(v.id);
      if (mins !== null) {
        c.bookedMinutes += mins;
        if (startsAm) c.amMinutes += mins;
        else c.pmMinutes += mins;
      }
    }
  }

  let busiestMinutes = 0;
  for (const c of cells.values())
    busiestMinutes = Math.max(busiestMinutes, c.bookedMinutes);

  return { lanes: [...lanes], days: [...days], cells, busiestMinutes };
}

/** The cell at (lane, day), or null when nothing is booked there. */
export function cellAt(grid: WeekGrid, laneId: string, day: string): WeekCell | null {
  return grid.cells.get(`${laneId}|${day}`) ?? null;
}

/**
 * A cell's bar width as a FRACTION of the busiest cell in view, or null when there is no
 * bar to draw.
 *
 * 🔴 Returns null — never 0 — when the week is empty or the cell has no booked minutes. A
 * zero-length bar dressed as data is decision 7's "—" in another form: the reader cannot
 * tell "nothing booked" from "booked, but tiny".
 */
export function loadFraction(
  cell: WeekCell | null,
  busiestMinutes: number,
): number | null {
  if (!cell || busiestMinutes <= 0 || cell.bookedMinutes <= 0) return null;
  return cell.bookedMinutes / busiestMinutes;
}

/**
 * A tech's whole week, for the panel's top level (A11).
 *
 * ⚠️ Summed over the SAME cells the grid drew, never re-derived from the visit list. Two
 * paths to one number is how a panel and the cell behind it start disagreeing while both
 * look right.
 */
export function laneWeekTotals(
  grid: WeekGrid,
  laneId: string,
): { stops: number; bookedMinutes: number; busiestDay: string | null } {
  let stops = 0;
  let bookedMinutes = 0;
  let busiestDay: string | null = null;
  let busiest = 0;
  for (const day of grid.days) {
    const c = cellAt(grid, laneId, day);
    if (!c) continue;
    stops += c.stops;
    bookedMinutes += c.bookedMinutes;
    if (c.bookedMinutes > busiest) {
      busiest = c.bookedMinutes;
      busiestDay = day;
    }
  }
  return { stops, bookedMinutes, busiestDay };
}

/** "3h 30m", "45m", or "—" when nothing was booked. Hours are what a dispatcher reads. */
export function hoursLabel(minutes: number): string {
  if (minutes <= 0) return '—';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h > 0 ? `${h}h${m > 0 ? ` ${m}m` : ''}` : `${m}m`;
}
